// src/store/appStore.ts

import { create } from 'zustand';
import type {
  Session,
  AwsCredentials,
  AccessPolicy,
  YearFolder,
  EventFolder,
  PhotoItem,
  PhotoPage,
} from '../models/types';
import { authService } from '../services/authService';
import { S3Service } from '../services/s3Service';
import { AccessPolicyService, getVisibleYears, getVisibleEvents } from '../services/accessPolicyService';
import { sortYearsDescending, sortEventsAscending, sortPhotosAscending } from '../utils/sorting';
import { paginate } from '../utils/pagination';
import { PHOTOS_PER_PAGE } from '../models/constants';

export interface AppState {
  // Auth
  session: Session | null;
  awsCredentials: AwsCredentials | null;
  isAuthenticated: boolean;
  loginError: string | null;

  // Access Policy
  accessPolicy: AccessPolicy | null;

  // Navigation data
  years: YearFolder[];
  events: EventFolder[];
  photos: PhotoPage | null;

  // UI
  isLoading: boolean;
  isOffline: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  fetchYears: () => Promise<void>;
  fetchEvents: (year: string) => Promise<void>;
  fetchPhotos: (year: string, event: string, page: number) => Promise<void>;
  refresh: () => Promise<void>;
  setOffline: (offline: boolean) => void;
  clearError: () => void;
}

const accessPolicyService = new AccessPolicyService();

export const useAppStore = create<AppState>((set, get) => ({
  // Initial state
  session: null,
  awsCredentials: null,
  isAuthenticated: false,
  loginError: null,
  accessPolicy: null,
  years: [],
  events: [],
  photos: null,
  isLoading: false,
  isOffline: false,
  error: null,

  login: async (email: string, password: string) => {
    set({ isLoading: true, loginError: null, error: null });

    try {
      const session = await authService.login(email, password);
      set({ session, isAuthenticated: true });

      const awsCredentials = await authService.getAwsCredentials(session.idToken);
      set({ awsCredentials });

      const accessPolicy = await accessPolicyService.fetchPolicy(session.idToken);
      set({ accessPolicy });

      // Fetch years after successful login
      const s3 = new S3Service(awsCredentials);
      const allYears = await s3.listYearPrefixes();
      const visibleYears = getVisibleYears(allYears, accessPolicy);
      const sortedYears = sortYearsDescending(visibleYears);

      // Count events per year
      const yearFolders: YearFolder[] = await Promise.all(
        sortedYears.map(async (year) => {
          const allEvents = await s3.listEventPrefixes(year);
          const visibleEvents = getVisibleEvents(year, allEvents, accessPolicy);
          return { name: year, eventCount: visibleEvents.length };
        })
      );

      set({ years: yearFolders, isLoading: false });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Login failed';
      // Re-throw NEW_PASSWORD_REQUIRED so the LoginPage can handle it
      if (message === 'NEW_PASSWORD_REQUIRED') {
        set({ isLoading: false });
        throw error;
      }
      set({ loginError: message, isLoading: false });
    }
  },

  logout: () => {
    authService.logout();
    set({
      session: null,
      awsCredentials: null,
      isAuthenticated: false,
      loginError: null,
      accessPolicy: null,
      years: [],
      events: [],
      photos: null,
      isLoading: false,
      error: null,
    });
  },

  fetchYears: async () => {
    const { awsCredentials, accessPolicy } = get();
    if (!awsCredentials || !accessPolicy) return;

    set({ isLoading: true, error: null });

    try {
      const s3 = new S3Service(awsCredentials);
      const allYears = await s3.listYearPrefixes();
      const visibleYears = getVisibleYears(allYears, accessPolicy);
      const sortedYears = sortYearsDescending(visibleYears);

      const yearFolders: YearFolder[] = await Promise.all(
        sortedYears.map(async (year) => {
          const allEvents = await s3.listEventPrefixes(year);
          const visibleEvents = getVisibleEvents(year, allEvents, accessPolicy);
          return { name: year, eventCount: visibleEvents.length };
        })
      );

      set({ years: yearFolders, isLoading: false });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to load years';
      set({ error: message, isLoading: false });
    }
  },

  fetchEvents: async (year: string) => {
    const { awsCredentials, accessPolicy } = get();
    if (!awsCredentials || !accessPolicy) return;

    set({ isLoading: true, error: null });

    try {
      const s3 = new S3Service(awsCredentials);
      const allEvents = await s3.listEventPrefixes(year);
      const visibleEvents = getVisibleEvents(year, allEvents, accessPolicy);
      const sortedEvents = sortEventsAscending(visibleEvents);

      // Count photos per event
      const eventFolders: EventFolder[] = await Promise.all(
        sortedEvents.map(async (event) => {
          const photos = await s3.listPhotoKeys(year, event);
          return { name: event, photoCount: photos.length, year };
        })
      );

      set({ events: eventFolders, isLoading: false });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to load events';
      set({ error: message, isLoading: false });
    }
  },

  fetchPhotos: async (year: string, event: string, page: number) => {
    const { awsCredentials } = get();
    if (!awsCredentials) return;

    set({ isLoading: true, error: null });

    try {
      const s3 = new S3Service(awsCredentials);
      const allKeys = await s3.listPhotoKeys(year, event);
      const sortedKeys = sortPhotosAscending(allKeys);

      const photoItems: PhotoItem[] = sortedKeys.map((key) => {
        const parts = key.split('/');
        const filename = parts[parts.length - 1];
        return { key, filename, year, event };
      });

      const paginationResult = paginate(photoItems, page, PHOTOS_PER_PAGE);

      const photoPage: PhotoPage = {
        photos: paginationResult.items,
        currentPage: paginationResult.currentPage,
        totalPages: paginationResult.totalPages,
        totalPhotos: paginationResult.totalItems,
      };

      set({ photos: photoPage, isLoading: false });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to load photos';
      set({ error: message, isLoading: false });
    }
  },

  refresh: async () => {
    const { photos, events, years } = get();

    // Re-fetch the most specific navigation context
    if (photos && photos.photos.length > 0) {
      const { year, event } = photos.photos[0];
      await get().fetchPhotos(year, event, photos.currentPage);
    } else if (events.length > 0) {
      const year = events[0].year;
      await get().fetchEvents(year);
    } else if (years.length > 0) {
      await get().fetchYears();
    }
  },

  setOffline: (offline: boolean) => {
    set({ isOffline: offline });
  },

  clearError: () => {
    set({ error: null });
  },
}));
